import { useEffect, useState } from 'react';

/**
 * Viewport query for the phone-only presentations. Everything that must switch
 * *component tree* (rather than just CSS) reads this one constant so the JS
 * breakpoint can never drift from the Tailwind `md` breakpoint it mirrors.
 *
 * Note this is deliberately a different breakpoint from the shell chrome, which
 * swaps the sidebar for the tab bar at `lg` (1024px): an iPad in portrait gets
 * the nicer mobile chrome but keeps the all-items audit runner it was designed
 * for. See the plan's "Breakpoints" decision.
 */
export const MOBILE_RUNNER_QUERY = '(max-width: 767px)';

/**
 * The `lg` boundary (1024px), for trees that must not MOUNT below it.
 *
 * Hiding with `hidden lg:block` is not enough on its own when the hidden subtree
 * fetches: the verifications source pane is CSS-hidden under `lg`, but a mounted
 * SourcePane still asks for a signed URL for the selected row, so every phone
 * visit to the queue paid for a document nobody had opened.
 */
export const BELOW_LG_QUERY = '(max-width: 1023px)';

// matchMedia is missing under SSR/prerender and in the node test environment.
// Treating that as "not mobile" keeps the desktop tree the safe default.
function queryMatches(query: string): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(query).matches;
}

/**
 * True while the viewport satisfies `query`, kept live via a `change` listener
 * so rotating a phone or resizing devtools re-renders the caller.
 *
 * Use this only where the two presentations genuinely differ in structure or
 * state (the audit runner); prefer Tailwind responsive classes everywhere else
 * so a single tree keeps serving both sizes.
 */
export function useIsMobile(query: string = MOBILE_RUNNER_QUERY): boolean {
  const [isMobile, setIsMobile] = useState(() => queryMatches(query));

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const list = window.matchMedia(query);
    // Re-read on mount: the viewport can change between the lazy initial state
    // and this effect (and `query` itself can change between renders).
    setIsMobile(list.matches);
    const onChange = (event: MediaQueryListEvent) => setIsMobile(event.matches);
    list.addEventListener('change', onChange);
    return () => list.removeEventListener('change', onChange);
  }, [query]);

  return isMobile;
}
